import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FaHeart, FaTimes, FaVideo, FaUsers, FaShare,
  FaPaperPlane, FaEye, FaClock, FaUserPlus, FaGift, FaThumbtack
} from 'react-icons/fa';
import api from '../utils/api';
import LiveGiftPanel from '../components/LiveGiftPanel';

const Live = () => {
  const { streamId } = useParams();
  const navigate = useNavigate();
  const [stream, setStream] = useState(null);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [likes, setLikes] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [showHearts, setShowHearts] = useState([]);
  const [duration, setDuration] = useState(0);
  const [isFollowing, setIsFollowing] = useState(false);
  const [pinnedComment, setPinnedComment] = useState(null);
  const [milestone, setMilestone] = useState(null);
  const prevViewerRef = useRef(0);
  const commentsEndRef = useRef(null);

  const MILESTONES = [10, 25, 50, 100, 250, 500, 1000];
  const MILESTONE_MSGS = {
    10: 'First 10 viewers!', 25: '25 viewers!', 50: '50 viewers!',
    100: '100 viewers!', 250: '250 viewers!', 500: '500 viewers!', 1000: '1K viewers!',
  };

  useEffect(() => {
    const fetchStream = async () => {
      try {
        const res = await api.get(`/live/${streamId}`);
        setStream(res.data.stream);
        setComments(res.data.comments || []);
        setLikes(res.data.likes || 0);
        setPinnedComment(res.data.pinned_comment || null);
        await api.post(`/live/${streamId}/join`);
      } catch {
        navigate('/dashboard');
      } finally {
        setIsLoading(false);
      }
    };
    fetchStream();

    const poll = setInterval(async () => {
      try {
        const res = await api.get(`/live/${streamId}`);
        if (res.data.stream.status === 'ended') { navigate('/dashboard'); return; }
        setStream(res.data.stream);
        setComments(res.data.comments || []);
        setLikes(res.data.likes || 0);
        setPinnedComment(res.data.pinned_comment || null);
        // Milestone detection
        const vc = res.data.stream.viewer_count || 0;
        const prev = prevViewerRef.current;
        for (const m of MILESTONES) {
          if (prev < m && vc >= m) {
            setMilestone(m);
            setTimeout(() => setMilestone(null), 5000);
            break;
          }
        }
        prevViewerRef.current = vc;
      } catch (err) { console.error('Failed to poll live updates', err); }
    }, 1500);

    return () => {
      clearInterval(poll);
      api.post(`/live/${streamId}/leave`).catch(() => {});
    };
  }, [streamId, navigate]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    commentsEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [comments]);

  // Timer
  useEffect(() => {
    if (isLoading || !stream) return;
    const t = setInterval(() => setDuration(d => d + 1), 1000);
    return () => clearInterval(t);
  }, [isLoading, stream]);

  const formatDuration = (s) => {
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = s % 60;
    return h > 0
      ? `${h}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`
      : `${m}:${String(sec).padStart(2, '0')}`;
  };

  const handleSendComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;
    try {
      const res = await api.post(`/live/${streamId}/comment`, { content: newComment });
      setComments(prev => [...prev, res.data]);
      setNewComment('');
    } catch (err) { console.error('Failed to send live comment', err); }
  };

  const handleLike = async () => {
    try {
      const res = await api.post(`/live/${streamId}/like`);
      setLikes(res.data.likes);
      const id = Date.now();
      setShowHearts(prev => [...prev, id]);
      setTimeout(() => setShowHearts(prev => prev.filter(h => h !== id)), 2500);
    } catch (err) { console.error('Failed to like stream', err); }
  };

  const handleClose = async () => {
    await api.post(`/live/${streamId}/leave`).catch(() => {});
    navigate('/dashboard');
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="fixed inset-0 bg-[#0B0F14] flex items-center justify-center z-50" data-testid="live-loading">
        <div className="text-center">
          <motion.div
            animate={{ scale: [1, 1.1, 1], opacity: [0.5, 1, 0.5] }}
            transition={{ duration: 1.5, repeat: Infinity }}
            className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#23cda0] via-[#2263f2] to-[#6c30ad] flex items-center justify-center mx-auto mb-4"
          >
            <FaVideo className="text-white text-xl" />
          </motion.div>
          <p className="text-white/60 text-sm font-medium">Joining stream...</p>
        </div>
      </div>
    );
  }

  if (!stream) return null;

  return (
    <div className="fixed inset-0 bg-[#0B0F14] z-50" data-testid="live-viewer-screen">
      <div className="relative h-full">
        {/* Background with thumbnail or gradient */}
        <div className="absolute inset-0">
          {stream.thumbnail_url ? (
            <div
              className="absolute inset-0"
              style={{
                backgroundImage: `url(${stream.thumbnail_url})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                filter: 'brightness(0.4) blur(2px)',
              }}
            />
          ) : (
            <div className="absolute inset-0 bg-gradient-to-br from-[#23cda0]/10 via-[#0B0F14] to-[#6c30ad]/10" />
          )}
        </div>

        {/* Gradient overlays */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-black/50 pointer-events-none" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/30 via-transparent to-transparent pointer-events-none" />

        {/* Mock Video Center */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="text-center">
            <motion.div
              animate={{ scale: [1, 1.06, 1] }}
              transition={{ duration: 3, repeat: Infinity }}
              className="w-28 h-28 rounded-3xl bg-gradient-to-br from-[#23cda0] via-[#2263f2] to-[#6c30ad] flex items-center justify-center mx-auto mb-4 shadow-2xl shadow-[#2263f2]/20"
            >
              <FaVideo className="text-white text-4xl" />
            </motion.div>
            <p className="text-white/30 text-sm font-medium">Live Stream</p>
            <p className="text-white/15 text-xs mt-1">Demo Mode</p>
          </div>
        </div>

        {/* ---- TOP BAR ---- */}
        <div className="absolute top-0 left-0 right-0 p-4 z-10">
          <div className="flex items-center justify-between">
            {/* Broadcaster card */}
            <div className="flex items-center space-x-3">
              <div className="flex items-center space-x-3 bg-black/40 backdrop-blur-xl rounded-2xl pr-4 pl-1.5 py-1.5 border border-white/5" data-testid="broadcaster-card">
                <div className="relative">
                  <img
                    src={stream.broadcaster_profile_picture || `https://ui-avatars.com/api/?name=${stream.broadcaster_username}&background=161B22&color=23cda0`}
                    alt={stream.broadcaster_username}
                    className="w-10 h-10 rounded-xl object-cover border border-white/10"
                  />
                  <div className="absolute -bottom-1 -right-1 w-3 h-3 bg-red-500 rounded-full border-2 border-[#0B0F14] animate-pulse" />
                </div>
                <div className="mr-2">
                  <p className="text-white font-bold text-sm leading-tight" data-testid="broadcaster-name">{stream.broadcaster_username}</p>
                  <p className="text-white/40 text-[10px]">{stream.viewer_count} watching</p>
                </div>
                <button
                  onClick={() => setIsFollowing(!isFollowing)}
                  data-testid="follow-btn"
                  className={`px-3 py-1 rounded-lg text-xs font-bold transition-all ${
                    isFollowing
                      ? 'bg-white/10 text-white/60'
                      : 'bg-gradient-to-r from-[#23cda0] to-[#2263f2] text-white hover:from-[#1db890] hover:to-[#1b56d9]'
                  }`}
                >
                  {isFollowing ? 'Following' : 'Follow'}
                </button>
              </div>
            </div>

            {/* Stats + Close */}
            <div className="flex items-center space-x-2">
              <div className="flex items-center space-x-1.5 bg-black/40 backdrop-blur-sm px-3 py-1.5 rounded-lg border border-white/5" data-testid="live-badge">
                <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                <span className="text-white font-bold text-xs tracking-wider">LIVE</span>
              </div>
              <div className="flex items-center space-x-1.5 bg-black/40 backdrop-blur-sm px-3 py-1.5 rounded-lg border border-white/5" data-testid="stream-timer">
                <FaClock className="text-white/50" size={10} />
                <span className="text-white/70 text-xs font-mono">{formatDuration(duration)}</span>
              </div>
              <div className="flex items-center space-x-1.5 bg-black/40 backdrop-blur-sm px-3 py-1.5 rounded-lg border border-white/5" data-testid="viewer-count">
                <FaEye className="text-[#23cda0]" size={10} />
                <span className="text-white/70 text-xs font-medium">{stream.viewer_count}</span>
              </div>
              <button
                onClick={handleClose}
                data-testid="close-stream-btn"
                className="bg-black/40 backdrop-blur-sm p-2 rounded-lg hover:bg-white/10 transition-colors border border-white/5"
              >
                <FaTimes className="text-white/60 hover:text-white" size={16} />
              </button>
            </div>
          </div>

          {/* Stream title */}
          <div className="mt-3 bg-black/30 backdrop-blur-sm rounded-xl px-4 py-2.5 max-w-md border border-white/5" data-testid="stream-info">
            <h3 className="text-white font-bold text-sm">{stream.title}</h3>
            {stream.description && <p className="text-white/40 text-xs mt-0.5">{stream.description}</p>}
          </div>
        </div>

        {/* ---- MILESTONE CELEBRATION ---- */}
        <AnimatePresence>
          {milestone && (
            <motion.div
              key={`milestone-${milestone}`}
              initial={{ opacity: 0, scale: 0.5, y: 50 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.8, y: -30 }}
              transition={{ type: 'spring', damping: 15 }}
              className="absolute top-1/3 left-1/2 -translate-x-1/2 z-30 pointer-events-none"
              data-testid="milestone-banner"
            >
              <div className="bg-gradient-to-r from-[#23cda0] via-[#2263f2] to-[#6c30ad] rounded-2xl px-8 py-5 shadow-2xl shadow-[#2263f2]/40 text-center">
                <motion.div
                  animate={{ rotate: [0, -10, 10, -10, 0] }}
                  transition={{ duration: 0.5, delay: 0.3 }}
                  className="text-4xl mb-2"
                >
                  {milestone >= 500 ? '🏆' : milestone >= 100 ? '🔥' : '🎉'}
                </motion.div>
                <p className="text-white font-bold text-xl">{MILESTONE_MSGS[milestone]}</p>
                <p className="text-white/70 text-sm mt-1">{stream.viewer_count} people watching</p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* ---- FLOATING HEARTS ---- */}
        <AnimatePresence>
          {showHearts.map((heartId) => (
            <motion.div
              key={heartId}
              initial={{ opacity: 1, y: 0, x: 0, scale: 1 }}
              animate={{ opacity: 0, y: -180, x: (Math.random() - 0.5) * 80, scale: 0.5 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 2.5, ease: 'easeOut' }}
              className="absolute bottom-36 right-16 text-2xl pointer-events-none"
              style={{ color: ['#23cda0', '#47aef0', '#2263f2', '#6c30ad'][Math.floor(Math.random() * 4)] }}
            >
              <FaHeart />
            </motion.div>
          ))}
        </AnimatePresence>

        {/* ---- BOTTOM: COMMENTS + ACTIONS ---- */}
        <div className="absolute bottom-0 left-0 right-0 p-4 z-10">
          {/* Pinned Comment Banner (Viewer - read only) */}
          {pinnedComment && (
            <div className="max-w-lg mb-3" data-testid="pinned-comment-banner">
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="flex items-start space-x-2 bg-[#23cda0]/15 backdrop-blur-xl rounded-xl px-3 py-2.5 border border-[#23cda0]/30"
              >
                <FaThumbtack className="text-[#23cda0] mt-0.5 flex-shrink-0" size={12} />
                <div className="flex-1 min-w-0">
                  <span className="text-[#23cda0] font-bold text-xs">{pinnedComment.username}</span>
                  <p className="text-white/90 text-sm leading-snug">{pinnedComment.content}</p>
                </div>
              </motion.div>
            </div>
          )}

          {/* Comments */}
          <div className="max-w-lg h-56 overflow-y-auto mb-4 scrollbar-hide" data-testid="live-comments">
            <div className="space-y-1.5">
              {comments.length === 0 && (
                <p className="text-white/20 text-sm py-6 text-center">Be the first to comment...</p>
              )}
              {comments.map((comment) => (
                <motion.div
                  key={comment.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  className="flex items-start space-x-2 bg-black/30 backdrop-blur-sm rounded-xl px-3 py-2 border border-white/5"
                >
                  <img
                    src={comment.profile_picture || `https://ui-avatars.com/api/?name=${comment.username}&background=161B22&color=23cda0`}
                    alt=""
                    className="w-7 h-7 rounded-full flex-shrink-0"
                  />
                  <div className="min-w-0">
                    <span className="text-[#23cda0] font-bold text-xs">{comment.username}</span>
                    <p className="text-white/80 text-sm leading-snug">{comment.content}</p>
                  </div>
                </motion.div>
              ))}
              <div ref={commentsEndRef} />
            </div>
          </div>

          {/* Actions Row */}
          <div className="flex items-center space-x-3 max-w-lg">
            {/* Comment Input */}
            <form onSubmit={handleSendComment} className="flex-1 flex items-center space-x-2">
              <input
                type="text"
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                placeholder="Say something..."
                className="flex-1 bg-white/10 backdrop-blur-sm text-white placeholder-white/30 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-[#23cda0]/50 border border-white/10"
                data-testid="live-comment-input"
              />
              <button
                type="submit"
                disabled={!newComment.trim()}
                className="bg-gradient-to-r from-[#23cda0] to-[#2263f2] text-white p-2.5 rounded-xl hover:from-[#1db890] hover:to-[#1b56d9] transition-all disabled:opacity-30 disabled:cursor-not-allowed"
                data-testid="send-live-comment"
              >
                <FaPaperPlane size={14} />
              </button>
            </form>

            {/* Like Button */}
            <button
              onClick={handleLike}
              data-testid="like-stream-btn"
              className="bg-white/10 backdrop-blur-sm p-3 rounded-xl hover:bg-white/20 transition-all relative border border-white/5 active:scale-90"
            >
              <FaHeart className="text-red-400" size={18} />
              {likes > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center">
                  {likes > 99 ? '99+' : likes}
                </span>
              )}
            </button>

            {/* Gift Button (Real PointPhoria gifting) */}
            {stream && <LiveGiftPanel streamId={stream.id} />}

            {/* Share Button */}
            <button
              data-testid="share-stream-btn"
              className="bg-white/10 backdrop-blur-sm p-3 rounded-xl hover:bg-white/20 transition-all border border-white/5"
            >
              <FaShare className="text-white/60" size={18} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Live;
